import { useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  atomFamily,
  atomWithStorage,
  createJSONStorage,
  unstable_withStorageValidator as withStorageValidator,
} from 'jotai/utils'
import * as Sentry from '@sentry/electron/renderer'
import { useUser } from '@renderer/hooks/useUser'
import {
  EMPTY_SORT_PREFERENCES,
  forgetSortItems,
  sortPreferencesSchema,
  updateSortPreference,
  type SortPreferences,
} from '@renderer/utils/sort-preferences'

const storage = withStorageValidator(
  (value: unknown): value is SortPreferences => sortPreferencesSchema.safeParse(value).success,
)(createJSONStorage())

const sortPreferencesAtom = atomFamily((userId: string | undefined) =>
  atomWithStorage(`highlight:sort-preferences:${userId ?? 'signed-out'}`, EMPTY_SORT_PREFERENCES, storage, {
    getOnInit: true,
  }),
)

export function useSortPreference(list: string): SortPreferences['lists'][string] | undefined {
  const preferences = useAtomValue(sortPreferencesAtom(useUser()?.id))
  return preferences.lists[list]
}

export function useSaveSortPreference(list: string): (order: ReadonlyArray<string>) => boolean {
  const userId = useUser()?.id
  const setPreferences = useSetAtom(sortPreferencesAtom(userId))
  return useCallback(
    (order: ReadonlyArray<string>): boolean => {
      if (!userId) {
        return false
      }
      try {
        setPreferences((current) => updateSortPreference(current, list, order))
        return true
      } catch (error) {
        Sentry.captureException(error)
        return false
      }
    },
    [list, setPreferences, userId],
  )
}

export function useForgetSortItems(list: string): (ids: ReadonlyArray<string>) => void {
  const setPreferences = useSetAtom(sortPreferencesAtom(useUser()?.id))
  return useCallback(
    (ids: ReadonlyArray<string>) => setPreferences((current) => forgetSortItems(current, list, ids)),
    [list, setPreferences],
  )
}
